import { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import {
  TextB,
  TextItalic,
  TextUnderline,
  ListBullets,
  ListNumbers,
  Quotes,
  TextHOne,
  TextHTwo,
  Paragraph as ParagraphIcon,
} from "@phosphor-icons/react";

/**
 * Iter 83 · TipTap-based rich-text editor for Current Reflection.
 *
 * Emits sanitized HTML on every change via `onChange(html)`. Uses the
 * ProseMirror document model internally so structure is preserved
 * even across cut/paste from Word/Docs. StarterKit gives us Heading
 * (h1–h3), Bold, Italic, Strike, BulletList, OrderedList, Blockquote,
 * Paragraph, and HardBreak. `Underline` is opt-in from a separate
 * package. Placeholder appears when the doc is empty.
 *
 * Paste handling: `transformPastedHTML` strips inline `style` attributes,
 * `class`, `id`, and any `<font>`/`<span>` wrappers that would otherwise
 * import foreign fonts and colors from Word/Docs. The remaining
 * whitelist is enforced server-side by `bleach` when the value is
 * persisted.
 *
 * The toolbar is intentionally spare — one restrained row of icon
 * toggles, no ribbon.
 */

const stripPastedInlineStyles = (html) => {
  if (typeof window === "undefined") return html;
  const template = document.createElement("template");
  template.innerHTML = html;
  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_ELEMENT);
  const doomed = [];
  while (walker.nextNode()) {
    const el = walker.currentNode;
    el.removeAttribute("style");
    el.removeAttribute("class");
    el.removeAttribute("id");
    el.removeAttribute("color");
    el.removeAttribute("face");
    el.removeAttribute("size");
    // Unwrap font/span wrappers that carry no semantic meaning.
    if (el.tagName === "FONT" || el.tagName === "SPAN") doomed.push(el);
  }
  doomed.forEach((el) => {
    const parent = el.parentNode;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  });
  return template.innerHTML;
};

const ToolbarButton = ({ active, onClick, label, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    data-testid={testid}
    aria-pressed={active}
    className={`h-8 px-2 flex items-center justify-center border-r border-border last:border-r-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
      active
        ? "text-primary bg-primary/10"
        : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
    }`}
  >
    {children}
  </button>
);

export default function RichReflectionEditor({ value, onChange, placeholder, testidBase = "rich-reflection" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // Explicit configuration keeps the ProseMirror schema tight —
        // no code blocks, no horizontal rules, no strike-through in
        // v1; that's what "restrained editorial" means.
        codeBlock: false,
        horizontalRule: false,
      }),
      Underline,
      Placeholder.configure({
        placeholder: placeholder || "Write the present considered judgment for this movement…",
        emptyEditorClass: "is-editor-empty",
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        // Prose styling class is applied by the parent so we don't
        // duplicate typography rules. `min-w-0` here belongs to the
        // ProseMirror surface itself.
        class: "focus:outline-none min-w-0",
        "data-testid": `${testidBase}-surface`,
        spellcheck: "true",
      },
      transformPastedHTML: stripPastedInlineStyles,
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Editor emits `<p></p>` when empty; treat that as empty string
      // for storage so the "Review & snapshot" button can disable.
      onChange?.(html === "<p></p>" ? "" : html);
    },
  });

  // Keep the editor in sync when the parent replaces the draft (e.g.
  // Cancel + reopen with the canonical body, or AI-drafted digest
  // populating the field).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (next !== current) {
      editor.commands.setContent(next, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="border border-primary/40 focus-within:border-primary bg-secondary/40" data-testid={testidBase}>
      <div className="flex items-center border-b border-border bg-card/60" role="toolbar" aria-label="Reflection formatting">
        <ToolbarButton
          active={editor.isActive("paragraph") && !editor.isActive("heading")}
          onClick={() => editor.chain().focus().setParagraph().run()}
          label="Paragraph"
          testid={`${testidBase}-btn-paragraph`}
        >
          <ParagraphIcon size={13} weight="regular" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("heading", { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          label="Heading 1"
          testid={`${testidBase}-btn-h1`}
        >
          <TextHOne size={14} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          label="Heading 2"
          testid={`${testidBase}-btn-h2`}
        >
          <TextHTwo size={14} weight="bold" />
        </ToolbarButton>
        <span aria-hidden className="w-px h-4 bg-border mx-1" />
        <ToolbarButton
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          label="Bold"
          testid={`${testidBase}-btn-bold`}
        >
          <TextB size={13} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          label="Italic"
          testid={`${testidBase}-btn-italic`}
        >
          <TextItalic size={13} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          label="Underline"
          testid={`${testidBase}-btn-underline`}
        >
          <TextUnderline size={13} weight="bold" />
        </ToolbarButton>
        <span aria-hidden className="w-px h-4 bg-border mx-1" />
        <ToolbarButton
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          label="Bulleted list"
          testid={`${testidBase}-btn-ul`}
        >
          <ListBullets size={13} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          label="Numbered list"
          testid={`${testidBase}-btn-ol`}
        >
          <ListNumbers size={13} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          label="Blockquote"
          testid={`${testidBase}-btn-quote`}
        >
          <Quotes size={13} weight="bold" />
        </ToolbarButton>
      </div>
      <EditorContent
        editor={editor}
        className="prose-reflection px-4 py-3 min-h-[180px] max-h-[60vh] overflow-y-auto overscroll-contain"
      />
    </div>
  );
}
